import React from "react";

import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { loadComponentModule } from "components/ComponentType";
import { Root, createRoot } from "react-dom/client";
import { queryClient } from "services/HttpService";
import { PrimeReactProvider } from "primereact/api";
import ErrorBoundary from "../components/ErrorBoundary";
import { useUserState } from "../store/user-store";
import { MissingComponentWarning } from "./MissingComponentWarning";
import { setupLocale } from "../services/lang/LanguageService";

export const COMPONENT_ATTRIBUTE = "jsComponent";
export const COMPONENT_SELECTOR = "[jsComponent]";
export const PROPS_ATTRIBUTE = "props";
const COMPONENT_IS_ROOT_FLAG = "_isComponentRoot";

// Component map [id -> HTML Element]
interface IReactRootEl {
  el: Element;
  root: Root;
}

const rootMap = new Map<string, IReactRootEl>();

/**
 * Renders a component into a DOM element. This is main entry point for rendering components.
 * @param root
 * @param importPromise Promise that resolves to a React component
 * @param props Props to pass to the component
 */
const renderComponent = (root: Root, importPromise: any, props: any) => {
  // @ts-ignore
  importPromise.then(async module => {
    const component = () => (
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <PrimeReactProvider value={{ zIndex: { overlay: 10000}}}>
            <ErrorBoundary>{React.createElement(module.default, props)}</ErrorBoundary>
          </PrimeReactProvider>

          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </React.StrictMode>
    );

    root.render(component());
  });
};

export const mountElementRoot = (el: Element, elType?: string, elProps?: any) => {
  const { id } = el;
  const type = elType ?? el.getAttribute(COMPONENT_ATTRIBUTE);
  const props = elProps ?? parseJsonProps(el.getAttribute(PROPS_ATTRIBUTE)!);

  if (rootMap.has(id)) {
    const oldRoot = rootMap.get(id);
    oldRoot!!.root.unmount();
    const oldType = oldRoot?.el.getAttribute(COMPONENT_ATTRIBUTE);

    console.log(`Re-mounted [${type}] component at [${id}]. Old component type [${oldType}].`);
  } else {
    console.log(`Mounting [${type}] component at [${id}].`);
  }

  // TODO: Handle webpack hot reload warnings
  const root = { el, root: createRoot(el, { identifierPrefix: `${id}_` }) };
  el.setAttribute(COMPONENT_IS_ROOT_FLAG, "true");

  rootMap.set(id, root);

  const importPromise = loadComponentModule(type!);

  if (importPromise) {
    renderComponent(root.root, importPromise, props);
  } else {
    root.root.render(<MissingComponentWarning componentId={type} />);
    console.log(`Unknown component! ${type}`);
  }
};

const parseJsonProps = (jsonProps: string) => {
  try {
    return JSON.parse(jsonProps);
  } catch {
    return {};
  }
};

export const initEasasJs = async () => {
  await setupLocale(useUserState.getState())

  const els = Array.from(document.querySelectorAll(COMPONENT_SELECTOR));

  const renderCallback = () => {
    if (unsub !== undefined) unsub();

    els.forEach(el => {
      mountElementRoot(el);
    });
  };

  let unsub: () => void;
  if (useUserState.getState().isCurrentUserLoaded) {
    renderCallback();
  } else {
    unsub = useUserState.subscribe(renderCallback);
  }
};
